import React, { useState } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Paper, 
  Typography, 
  Grid, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Alert,
  CircularProgress,
  Snackbar
} from '@mui/material';
import { useData } from '../../contexts/DataContext';
import { useNavigate } from 'react-router-dom';

const REFERRAL_SOURCES = [
  'Google Search',
  'Referral',
  'Social Media',
  'Website',
  'Yelp',
  'Angie\'s List',
  'HomeAdvisor',
  'Other'
];

export default function CustomerForm({ onSubmit, initialData }) {
  const navigate = useNavigate();
  const { addCustomer } = useData();
  const [formData, setFormData] = useState(initialData || {
    name: '',
    address: '',
    city: '',
    zipCode: '',
    phone: '',
    email: '',
    referralSource: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.address.trim()) errors.address = 'Address is required';
    if (!formData.city.trim()) errors.city = 'City is required';
    if (!formData.zipCode.trim()) errors.zipCode = 'ZIP Code is required';
    if (!formData.phone.trim()) errors.phone = 'Phone is required';
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError(null);
      
      const customerData = {
        ...formData,
        createdBy: initialData?.createdBy || 'Admin',
        createdAt: initialData?.createdAt || new Date().toISOString(),
      };
      
      if (onSubmit) {
        await onSubmit(customerData);
      } else {
        const newCustomer = await addCustomer(customerData);
        setSuccess(true);
        setTimeout(() => {
          navigate(`/estimates/new?customerId=${newCustomer.id}`);
        }, 1500);
      }
    } catch (err) {
      console.error('Error saving customer:', err);
      setError(err.message || 'Failed to save customer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
      <Typography variant="h6" gutterBottom>
        {initialData ? 'Edit Customer' : 'New Customer'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <TextField
          margin="normal"
          required
          fullWidth
          id="name"
          label="Customer Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          error={!!validationErrors.name}
          helperText={validationErrors.name}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          id="address"
          label="Street Address"
          name="address"
          value={formData.address}
          onChange={handleChange}
          multiline
          rows={2}
          error={!!validationErrors.address}
          helperText={validationErrors.address}
        />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="city"
              label="City"
              name="city"
              value={formData.city}
              onChange={handleChange}
              error={!!validationErrors.city}
              helperText={validationErrors.city}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="zipCode"
              label="ZIP Code"
              name="zipCode"
              value={formData.zipCode}
              onChange={handleChange}
              error={!!validationErrors.zipCode}
              helperText={validationErrors.zipCode}
            />
          </Grid>
        </Grid>
        <TextField
          margin="normal"
          required
          fullWidth
          id="phone"
          label="Phone Number"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          error={!!validationErrors.phone}
          helperText={validationErrors.phone}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          id="email"
          label="Email Address"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          error={!!validationErrors.email}
          helperText={validationErrors.email}
        />
        <FormControl fullWidth margin="normal">
          <InputLabel id="referral-source-label">How did you find us?</InputLabel>
          <Select
            labelId="referral-source-label"
            id="referralSource"
            name="referralSource"
            value={formData.referralSource}
            label="How did you find us?"
            onChange={handleChange}
          >
            {REFERRAL_SOURCES.map((source) => (
              <MenuItem key={source} value={source}>
                {source}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? (
              <>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                {initialData ? 'Updating...' : 'Adding...'}
              </>
            ) : (
              initialData ? 'Update Customer' : 'Add Customer'
            )}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/customers')}
            disabled={loading}
          >
            Cancel
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={success}
        autoHideDuration={3000}
        onClose={() => setSuccess(false)}
        message="Customer saved successfully!"
      />
    </Paper>
  );
} 